import { useEffect, useState } from "react";
import api from "../lib/api";
import { partTimePct } from "../lib/contract";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { FileSignature, History, Plus, Loader2, FileSearch } from "lucide-react";
import { ContractChangeDialog } from "./ContractChangeDialog";
import { GenerateContractDialog } from "./GenerateContractDialog";
import { UnilavDialog } from "./UnilavDialog";
import { ProrogaLetterDialog } from "./ProrogaLetterDialog";

const fmtDate = (iso) => {
    if (!iso || iso === "1900-01-01") return "—";
    try {
        return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch {
        return iso;
    }
};

export const ContractHistoryPanel = ({ employee, onUpdated, onContractSaved }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [changeOpen, setChangeOpen] = useState(false);
    const [generateOpen, setGenerateOpen] = useState(false);
    const [unilavOpen, setUnilavOpen] = useState(false);
    const [prorogaOpen, setProrogaOpen] = useState(false);
    const [letterKind, setLetterKind] = useState("proroga");
    const isIndeterminato = /indeterminato/i.test(employee?.contratto_tipo || "");

    const fetchHistory = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/employees/${employee.id}/contracts`);
            setItems(data);
        } catch (e) {
            // silent
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (employee?.id) fetchHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employee?.id]);

    return (
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="contract-history-panel">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 font-display text-base font-semibold text-[#376DB3]">
                    <History className="h-4 w-4" />
                    Storico contratti
                </h3>
                <div className="flex flex-wrap items-center gap-1.5">
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setGenerateOpen(true)}
                        className="gap-1.5"
                        data-testid="generate-contract-btn"
                    >
                        <FileSignature className="h-4 w-4" />
                        Genera contratto
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setUnilavOpen(true)}
                        className="gap-1.5"
                        data-testid="unilav-btn"
                    >
                        <FileSearch className="h-4 w-4" />
                        UNILAV
                    </Button>
                    {employee?.contratto_scadenza && !employee?.data_cessazione && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setLetterKind("proroga"); setProrogaOpen(true); }}
                            className="gap-1.5"
                            data-testid="proroga-letter-btn"
                        >
                            <FileSignature className="h-4 w-4" />
                            Comunicazione proroga
                        </Button>
                    )}
                    {isIndeterminato && !employee?.data_cessazione && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setLetterKind("trasformazione"); setProrogaOpen(true); }}
                            className="gap-1.5"
                            data-testid="trasformazione-letter-btn"
                        >
                            <FileSignature className="h-4 w-4" />
                            Comunicazione trasformazione
                        </Button>
                    )}
                    <Button
                        size="sm"
                        onClick={() => setChangeOpen(true)}
                        className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                        data-testid="contract-change-btn"
                    >
                        <Plus className="h-4 w-4" />
                        Modifica contratto
                    </Button>
                </div>
            </div>

            <div className={`flex justify-center py-4 ${loading ? "" : "hidden"}`}>
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>

            <div className={`py-6 text-center text-sm text-slate-400 ${!loading && items.length === 0 ? "" : "hidden"}`}>
                Nessuna modifica registrata. Il contratto iniziale verrà mostrato qui automaticamente.
            </div>

            <ol className={`relative space-y-4 border-l-2 border-slate-200 pl-5 ${!loading && items.length > 0 ? "" : "hidden"}`}>
                {items.map((c) => (
                    <li key={c.id} className="relative" data-testid={`contract-entry-${c.id}`}>
                        <span
                            className={`absolute -left-[27px] top-1 flex h-4 w-4 items-center justify-center rounded-full ring-4 ring-white ${
                                c.active ? "bg-emerald-500" : "bg-slate-300"
                            }`}
                            aria-hidden
                        />
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="font-display font-semibold text-slate-800">{c.contratto_tipo}</span>
                            {c.contratto_ore != null && (
                                <Badge variant="secondary" className="font-mono-numbers">
                                    {c.contratto_ore}h/sett.{partTimePct(c.contratto_tipo, c.contratto_ore) ? ` (${partTimePct(c.contratto_tipo, c.contratto_ore)})` : ""}
                                </Badge>
                            )}
                            {c.active && (
                                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Attivo</Badge>
                            )}
                            {c.contratto_scadenza && (
                                <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 text-[10px]">
                                    Scad. {fmtDate(c.contratto_scadenza)}
                                </Badge>
                            )}
                        </div>
                        <div className="mt-1 text-xs text-slate-600 font-mono-numbers">
                            Dal {fmtDate(c.decorrenza)}
                            {c.fine && ` al ${fmtDate(c.fine)}`}
                        </div>
                        {c.motivo && (
                            <div className="mt-1 text-xs text-slate-500 italic">"{c.motivo}"</div>
                        )}
                        {c.created_by && c.created_by !== "system" && (
                            <div className="mt-0.5 text-[10px] text-slate-400">Registrato da {c.created_by}</div>
                        )}
                    </li>
                ))}
            </ol>

            <ContractChangeDialog
                open={changeOpen}
                onOpenChange={setChangeOpen}
                employee={employee}
                onSaved={async () => {
                    await fetchHistory();
                    onUpdated && onUpdated();
                }}
            />
            <GenerateContractDialog
                open={generateOpen}
                onOpenChange={setGenerateOpen}
                employee={employee}
                onSaved={onContractSaved}
            />
            <UnilavDialog
                open={unilavOpen}
                onOpenChange={setUnilavOpen}
                employee={employee}
                onUpdated={onUpdated}
            />
            <ProrogaLetterDialog
                open={prorogaOpen}
                onOpenChange={setProrogaOpen}
                employeeId={employee?.id}
                kind={letterKind}
                onDone={onContractSaved || onUpdated}
            />
        </div>
    );
};
